import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Wind, Thermometer, Battery, Activity, Shield } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const Superconductor = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  const technologies = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Zero Resistance',
      description: 'Perfect electrical conductivity at cryogenic temperatures',
      metric: '0Ω',
      label: 'Resistance',
    },
    {
      icon: <Thermometer className="w-8 h-8" />,
      title: 'Cryogenic Systems',
      description: 'Advanced cooling technology maintaining ultra-low temperatures',
      metric: '<100mK',
      label: 'Operating Temp',
    },
    {
      icon: <Wind className="w-8 h-8" />,
      title: 'Magnetic Levitation',
      description: 'Meissner effect enabling perfect diamagnetism',
      metric: '100%',
      label: 'Field Expulsion',
    },
    {
      icon: <Battery className="w-8 h-8" />,
      title: 'Energy Efficiency',
      description: 'Minimal energy loss in quantum circuits and transmission',
      metric: '99.9%',
      label: 'Efficiency',
    },
  ];

  const materials = [
    {
      name: 'Niobium (Nb)',
      temp: '9.2K',
      applications: ['Quantum circuits', 'SRF cavities', 'MRI magnets'],
    },
    {
      name: 'Niobium-Titanium (NbTi)',
      temp: '10K',
      applications: ['LHC magnets', 'Fusion reactors', 'Medical imaging'],
    },
    {
      name: 'Niobium-Tin (Nb3Sn)',
      temp: '18K',
      applications: ['High-field magnets', 'Particle accelerators'],
    },
    {
      name: 'YBCO Cuprates',
      temp: '92K',
      applications: ['Power transmission', 'High-temp applications'],
    },
  ];

  const applications = [
    {
      icon: <Activity className="w-6 h-6" />,
      title: 'Quantum Computing',
      description: 'Superconducting qubits for fault-tolerant quantum processors',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Power Grid',
      description: 'Loss-free transmission of electricity over long distances',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Medical Imaging',
      description: 'High-field MRI systems for advanced diagnostics',
    },
    {
      icon: <Wind className="w-6 h-6" />,
      title: 'Magnetic Levitation',
      description: 'Frictionless transport systems and bearings',
    },
  ];

  const advantages = [
    'Perfect conductivity with zero energy loss',
    'Enables ultra-high magnetic fields',
    'Critical for quantum computing infrastructure',
    'Reduces power consumption in data centers',
    'Enables revolutionary medical technologies',
    'Foundation for future fusion energy',
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes magnetic-wave {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.5); opacity: 0.6; }
        }
        @keyframes float-circle {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-magnetic-wave {
          animation: magnetic-wave 4s ease-in-out infinite;
        }
        .animate-float-circle {
          animation: float-circle 8s ease-in-out infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[70vh] flex items-center ${classes.bg} overflow-hidden`}>
        {/* Magnetic Field Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="field-gradient">
              <stop offset="0%" stopColor={theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} />
              <stop offset="100%" stopColor={theme === 'light' ? '#8b5cf6' : theme === 'dark' ? '#3b82f6' : '#ec4899'} />
            </linearGradient>
          </defs>
          {[...Array(6)].map((_, i) => (
            <path
              key={i}
              d={`M 0 ${20 + i * 15}% Q 50 ${15 + i * 15}%, 100 ${20 + i * 15}%`}
              stroke="url(#field-gradient)"
              strokeWidth="2"
              fill="none"
              opacity="0.5"
            />
          ))}
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-up">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
                Superconducting{' '}
                <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                  Systems
                </span>
              </h1>
              <p className={`text-lg sm:text-xl ${classes.subtext} mb-8`}>
                Harnessing the power of zero-resistance materials for quantum computing, energy transmission,
                and revolutionary technologies that reshape our future.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
                >
                  Explore Solutions
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/research"
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                    theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' :
                    theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' :
                    'border-purple-500 text-purple-400 hover:bg-purple-950'
                  } transition-all`}
                >
                  Research Papers
                </Link>
              </div>
            </div>

            {/* Levitation Visualization */}
            <div className="relative h-64 sm:h-96 lg:h-[400px] flex items-center justify-center animate-fade-in">
              <div className="relative w-full h-full flex items-center justify-center">
                {/* Magnetic Field Rings */}
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute rounded-full border-2 ${classes.cardBorder} opacity-30 animate-magnetic-wave`}
                    style={{
                      width: `${200 + i * 60}px`,
                      height: `${200 + i * 60}px`,
                      animationDelay: `${i * 0.5}s`,
                    }}
                  />
                ))}
                
                {/* Levitating Object */}
                <div className="relative animate-float-circle">
                  <div className={`w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-to-r ${classes.accent} shadow-2xl`}>
                    <div className="absolute inset-0 rounded-full bg-white opacity-20" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Zap className="w-16 h-16 sm:w-20 sm:h-20 text-white" />
                    </div>
                  </div>
                </div>

                {/* Base Platform */}
                <div className={`absolute bottom-8 w-48 h-3 sm:w-64 sm:h-4 ${classes.cardBg} border ${classes.cardBorder} rounded-full shadow-lg`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Key Properties
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Unique characteristics of superconducting materials
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} text-center animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                  {tech.icon}
                </div>
                <h3 className={`text-xl font-bold ${classes.text} mb-2`}>{tech.title}</h3>
                <p className={`${classes.subtext} text-sm mb-4`}>{tech.description}</p>
                <div className={`text-3xl font-bold ${classes.accentText} mb-1`}>{tech.metric}</div>
                <div className={`text-xs ${classes.subtext}`}>{tech.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Superconducting Materials
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Advanced materials powering quantum technologies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {materials.map((material, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className={`text-xl font-bold ${classes.text}`}>{material.name}</h3>
                  <span className={`px-3 py-1 text-sm rounded-full bg-gradient-to-r ${classes.accent} text-white`}>
                    Tc: {material.temp}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {material.applications.map((app, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1 text-xs sm:text-sm rounded-full ${classes.cardBg} border ${classes.cardBorder} ${classes.text}`}
                    >
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Real-World Applications
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                  {app.icon}
                </div>
                <h3 className={`text-lg font-bold ${classes.text} mb-2`}>{app.title}</h3>
                <p className={`text-sm ${classes.subtext}`}>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Why Superconductors Matter
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {advantages.map((advantage, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${classes.cardBg} backdrop-blur-md rounded-xl p-4 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Zap className={`w-5 h-5 ${classes.accentText} flex-shrink-0 mt-0.5`} />
                <span className={`${classes.text} text-sm sm:text-base`}>{advantage}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Transform Your Technology
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Discover how superconducting systems can revolutionize your applications
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
          >
            Contact Our Experts
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Superconductor;